import React, { useCallback, useEffect, useState } from "react";

import moment from "moment";

import { Box, Breadcrumbs, Button, Card, CardContent, Chip, Container, Grid, Link, Pagination, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material";

import StatisticalCard from "../components/StatisticalCard";
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import functionsApiPublic from "../services/apiPublic.service";
import OrderInterface from "../interfaces/OrderInterface";
import statusProps from "../utils/StatusUtils";


const Reports: React.FC = () => {
	const [startDateFilter, setStartDateFilter] = useState<Date>(
		moment().startOf('month').toDate()
	);
	const [endDateFilter, setEndDateFilter] = useState<Date>(
		moment().endOf('month').add(-1, 'milliseconds').toDate()
	);

	// Report Data
	const [reportsResponse, setReportsResponse] = useState<{
		PRICE_SUM: number;
		SERVICE_FEE_SUM: number;
		FINISHED_COUNT: number;
		CANCELED_COUNT: number;
		PRICE_AVERAGE: number;
		DISTANCE_SUM: number;
		object: string;
		totalCount: number;
		limit: number;
		offset: number;
		data: OrderInterface[];
	}>();
	const [loadingReports, setLoadingReports] = useState<boolean>(false);

	const [ordersOffset, setOrdersOffset] = useState<number>(0);

	const isFilterWithError = (): boolean => {
		return (startDateFilter > endDateFilter)
	}

	const handleClickFilterButton = async () => {
		try {
			fetchReportsData();
		} catch (error){
			console.log(error);
		}
	}

	const fetchReportsData = useCallback(async () => {
		try {
			setLoadingReports(true);
			setTimeout(async () => {
				await functionsApiPublic.fetchReportsData(
					startDateFilter,
					endDateFilter,
					ordersOffset
				).then((response) => {
					setReportsResponse(response.data);
					setOrdersOffset(response.data.offset);
				}).finally(() => {
					setLoadingReports(false);
				})
			}, 1000);
		} catch (error){
			console.log(error);
		}
	}, [
		startDateFilter,
		endDateFilter,
		ordersOffset
	]);

	useEffect(() => {
		fetchReportsData();
	}, [
		fetchReportsData
	]);

	return (
		<>
			<Box m={2}>
				<Breadcrumbs aria-label="breadcrumb">
					<Link underline="hover" color="inherit" href="/">
						Página inicial
					</Link>
					<Typography color="text.primary" aria-current="page">Relatórios</Typography>
				</Breadcrumbs>
			</Box>
			<Container>
				<Box mt={3} mb={3}>
					<Typography variant="h4">
						Relatórios
					</Typography>
					<Typography variant="subtitle1">
						Veja informações e relatórios referentes a sua utilização na plataforma.
					</Typography>
				</Box>

				<Grid container spacing={2}>
					<Grid item xs={12} md={4} lg={4}>
						<LocalizationProvider dateAdapter={AdapterMoment}>
							<DatePicker
								label="Data inicial"
								value={startDateFilter}
								onChange={(newValue) => {
									if (newValue !== null){
										setStartDateFilter(newValue);
									}
								}}
								renderInput={(params) => <TextField {...params} fullWidth size="small" />}
							/>
						</LocalizationProvider>
					</Grid>

					<Grid item xs={12} md={4} lg={4}>
						<LocalizationProvider dateAdapter={AdapterMoment}>
							<DatePicker
								label="Data final"
								value={endDateFilter}
								onChange={(newValue) => {
									if (newValue !== null){
										setEndDateFilter(newValue);
									}
								}}
								renderInput={(params) => <TextField {...params} fullWidth size="small" />}
							/>
						</LocalizationProvider>
					</Grid>

					<Grid item xs={12} md={4} lg={4}>
						<Button
							fullWidth
							variant="contained"
							size="large"
							onClick={handleClickFilterButton}
							disabled={isFilterWithError()}
						>
							Filtrar
						</Button>
					</Grid>
				</Grid>
				<Grid container spacing={2} mt={2} mb={10}>
					<Grid item lg={4} md={4} xs={6}>
						<StatisticalCard
							title={`Valor utilizado`}
							value={Number(reportsResponse?.PRICE_SUM).toLocaleString("pt-BR", {style: "currency", currency: "BRL"})}
							isLoading={loadingReports}
							bottomText={`${Number(reportsResponse?.SERVICE_FEE_SUM).toLocaleString("pt-BR", {style: "currency", currency: "BRL"})} de taxa`}
						/>
					</Grid>
					<Grid item lg={4} md={4} xs={6}>
						<StatisticalCard
							title={`Pedidos entregues`}
							value={`${Number(reportsResponse?.FINISHED_COUNT)}`}
							isLoading={loadingReports}
							bottomText={`${Number(reportsResponse?.CANCELED_COUNT)} cancelados`}
						/>
					</Grid>
					<Grid item lg={4} md={4} xs={6}>
						<StatisticalCard
							title={`Valor médio`}
							value={Number(reportsResponse?.PRICE_AVERAGE).toLocaleString("pt-BR", {style: "currency", currency: "BRL"})}
							isLoading={loadingReports}
							bottomText={`${Number(reportsResponse?.DISTANCE_SUM).toFixed(2)} km de distância`}
						/>
					</Grid>
					<Grid item lg={12} md={12} xs={12}>
						<Card className="beaultifulCard">
							<CardContent>
								<Pagination
									disabled={loadingReports}
									count={(Math.ceil((reportsResponse?.totalCount ?? 0) / (reportsResponse?.limit ?? 0)) ?? 0)}
									page={(reportsResponse?.offset === 0 ? 1 : Math.ceil(reportsResponse?.offset ?? 1) / (reportsResponse?.limit ?? 1) + 1)}
									onChange={(event: React.ChangeEvent<unknown>, value: number) => {
										if (reportsResponse !== undefined){
											setOrdersOffset((value - 1) * reportsResponse?.limit ?? 10);
										}
									}}
								/>

								{/* <p>{JSON.stringify(reportsResponse?.data)}</p> */}

								{reportsResponse?.data !== undefined ? <>
									<TableContainer>
										<Table size={"medium"}>
											<TableHead>
												<TableRow>
													<TableCell>DATA</TableCell>
													<TableCell>STATUS</TableCell>
													<TableCell>TOTAL</TableCell>
													<TableCell>TAXA</TableCell>
													<TableCell></TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{reportsResponse?.data?.map((order) => (
													<TableRow hover key={order?._id}>
														<TableCell>
															{moment(order.startTime).format("DD/MM/YYYY - HH:mm:ss")}
														</TableCell>
														<TableCell>
															<Chip
																variant={"outlined"}
																size={"small"}
																label={statusProps.filter(function(status){return status.status === order.status})[0].text}
																color={"primary"}
																style={{
																	backgroundColor: statusProps.filter(function(status){return status.status === order.status})[0].backgroundColor,
																	color: statusProps.filter(function(status){return status.status === order.status})[0].textColor,
																	borderWidth: 0
																}}
															/>
														</TableCell>
														<TableCell>
															{order?.fareDetails?.price?.toLocaleString("pt-BR", {
																style: "currency",
																currency: "BRL"
															})}
														</TableCell>
														<TableCell>
															{order?.fareDetails?.serviceFee?.toLocaleString("pt-BR", {
																style: "currency",
																currency: "BRL"
															})}
														</TableCell>
														<TableCell>
															<Button
																size={"small"}
																href={`/order/${order.id}`}
															>
																Visualizar
															</Button>
														</TableCell>
													</TableRow>
												))}
											</TableBody>
										</Table>
									</TableContainer>
								</> : <>
								
								</>}
								
								<Pagination
									disabled={loadingReports}
									count={(Math.ceil((reportsResponse?.totalCount ?? 0) / (reportsResponse?.limit ?? 0)) ?? 0)}
									page={(reportsResponse?.offset === 0 ? 1 : Math.ceil(reportsResponse?.offset ?? 1) / (reportsResponse?.limit ?? 1) + 1)}
									onChange={(event: React.ChangeEvent<unknown>, value: number) => {
										if (reportsResponse !== undefined){
											setOrdersOffset((value - 1) * reportsResponse?.limit ?? 10);
										}
									}}
								/>
							</CardContent>
						</Card>
					</Grid>
				</Grid>
			</Container>
		</>
	)
};

export default Reports;